import { type TablePaginationConfig } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../utils/cn.ts"
import {
  getTableOrderingFromSorter,
  withSortOrder
} from "../../../../utils/table"
import type {
  StatusChangeOptions,
  StatusChangeOutcome
} from "../../../Project/components/ProjectStatusSelector.tsx"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { ProjectsEmpty } from "../../components/ProjectsEmpty.tsx"
import { PROJECT_STATUSES_OBJ } from "../../constants/project-statuses.ts"
import {
  type ProjectBulkResult,
  type ProjectFilters
} from "../../redux/projects.types.ts"
import { type ProjectListItem } from "../../types/types.ts"
import {
  type ProjectEditableRowData,
  ProjectListEditableRow
} from "./ProjectListEditableRow.tsx"
import {
  ProjectsBulkActionBar,
  type ProjectBulkActionParams
} from "./ProjectsBulkActionBar.tsx"
import { PROJECT_COLUMN_KEYS, getColumns } from "./columns.tsx"

const DEFAULT_HIDDEN_COLUMNS = [PROJECT_COLUMN_KEYS.endDate]

// Its own key: the Archive renders two columns Active does not, and a shared key
// would apply a saved visibility and order to columns that are not there.
const PROJECT_ARCHIVE_SURFACE = `${TABLE_SURFACES.projects}-archive`

interface ProjectListTableProps {
  loading: boolean
  total: number
  projectPermissions: PermissionProjectPermissions
  ordering: string | null
  filters: ProjectFilters
  filterOptions: Record<string, BaseOptionType[]>
  projects: ProjectListItem[]
  next?: string | null
  retryNext?: string | null
  onLoadMore: () => void
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
  onStatusChange: (
    id: string,
    status: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome>
  archived?: boolean
  // Bulk-action wiring — supplied by the Active list only.
  selectedIds?: string[]
  onSelectedIdsChange?: (ids: string[]) => void
  onBulkAction?: (
    params: ProjectBulkActionParams
  ) => Promise<ProjectBulkResult>
}

export function ProjectListTable({
  projects,
  loading,
  projectPermissions,
  ordering,
  filters,
  total,
  filterOptions,
  next,
  retryNext,
  onLoadMore,
  onOrderingChange,
  onSelectRow,
  onStatusChange,
  archived = false,
  selectedIds = [],
  onSelectedIdsChange,
  onBulkAction
}: ProjectListTableProps) {
  const { t } = useTranslation()
  const [editingID, setEditingID] = useState<string>("")

  const moduleAccess = projectPermissions?.projects
  const moduleFeatures = projectPermissions?.features
  const showProgress = !!moduleFeatures?.project_show_progress
  const showBudget = !!moduleFeatures?.project_show_budget

  const onChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ProjectListItem> | SorterResult<ProjectListItem>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: loading,
    next: next,
    loadMore: onLoadMore,
    fullPage: true
  })

  const tableSettings = useMemo(
    () => ({
      onRow: (record: ProjectListItem) => {
        return {
          record,
          editingID,
          assignedPMAllowed: moduleFeatures?.project_assign_pm,
          filterOptions,
          onCancelEdit: () => setEditingID(""),
          onUpdateRow: (record: ProjectEditableRowData) =>
            onSelectRow(record, "update"),
          ...(moduleAccess?.project_edit
            ? {
                onDoubleClick: () => {
                  if (editingID) return
                  return setEditingID(record?.id)
                }
              }
            : {})
        }
      }
    }),
    [
      moduleFeatures?.project_assign_pm,
      moduleAccess?.project_edit,
      onSelectRow,
      editingID,
      filterOptions
    ]
  )

  const tableColumns = useMemo(
    () =>
      withSortOrder(
        getColumns({
          t,
          onStatusChange,
          onSelectRow,
          editAllowed: !!moduleAccess?.project_edit,
          projectPermissions,
          archived,
          archiveAllowed: !!moduleFeatures?.project_archive,
          restoreAllowed: !!moduleFeatures?.project_archive,
          deleteAllowed: !!moduleAccess?.project_delete,
          showProgress,
          showBudget,
          statuses: filterOptions?.project_status || []
        }),
        ordering
      ),
    [
      t,
      ordering,
      filterOptions?.project_status,
      projectPermissions,
      moduleAccess?.project_edit,
      moduleAccess?.project_delete,
      moduleFeatures?.project_archive,
      archived,
      showProgress,
      showBudget,
      onSelectRow,
      onStatusChange
    ]
  )

  // Measured, not declared: the Archive columns are `hidden` rather than absent
  // from the array, and Sales Price and Progress come and go with permissions,
  // so any constant here is wrong on every layout but one.
  const scrollX = useMemo(
    () =>
      tableColumns.reduce(
        (total, column) =>
          column.hidden ? total : total + (Number(column.width) || 0),
        0
      ),
    [tableColumns]
  )

  // The Archive keeps `EntityTable`'s own on-demand selection (shown, never
  // acted on). The Active list lifts the ids so its bulk bar can use them.
  const bulkEnabled = !archived && !!onBulkAction && !!onSelectedIdsChange

  const rowSelection = useMemo(
    () =>
      bulkEnabled
        ? {
            selectedRowKeys: selectedIds,
            preserveSelectedRowKeys: true as const,
            onChange: (keys: React.Key[]) =>
              onSelectedIdsChange?.(keys.map(String)),
            // A selection cannot be edited while a row is in inline edit; the
            // ticks it already holds stay visible, they just cannot change.
            getCheckboxProps: () => ({ disabled: !!editingID })
          }
        : undefined,
    [bulkEnabled, selectedIds, onSelectedIdsChange, editingID]
  )

  const bulkStatusOptions = useMemo(
    () =>
      Object.values(PROJECT_STATUSES_OBJ).map(({ label, value }) => ({
        label,
        value
      })),
    []
  )

  const projectManagerOptions = useMemo(
    () =>
      (filterOptions?.project_manager ?? []).map((option) => ({
        label: String(option.label ?? ""),
        value: String(option.value ?? "")
      })),
    [filterOptions?.project_manager]
  )

  const projectNames = useMemo(
    () =>
      Object.fromEntries(
        (projects ?? []).map((project) => [project.id, project.lead_name])
      ),
    [projects]
  )

  return (
    <div ref={tableRef}>
      <EntityTable<ProjectListItem>
        surface={archived ? PROJECT_ARCHIVE_SURFACE : TABLE_SURFACES.projects}
        selectionMode="on-demand"
        selectionFilters={filters}
        rowSelection={rowSelection}
        tab={DEFAULT_TAB}
        components={{
          body: projects?.length ? { row: ProjectListEditableRow } : {}
        }}
        columns={tableColumns}
        defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
        loading={loading && !projects?.length}
        dataSource={projects}
        scroll={{
          x: scrollX,
          scrollToFirstRowOnChange: true,
          y: tableHeight
        }}
        onChange={onChangeTable}
        onScroll={onTableScroll}
        withOutPagination
        pagination={false}
        onRow={tableSettings.onRow}
        rowHoverable={true}
        locale={{
          emptyText: <ProjectsEmpty hasSearch={!!filters?.q} />
        }}
        className={cn({
          "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
            !projects?.length
        })}
      />
      <TableResultCount
        loaded={projects?.length ?? 0}
        total={total}
        loading={loading}
        next={next}
        retryNext={retryNext}
        onRetry={onLoadMore}
      />
      {bulkEnabled ? (
        <ProjectsBulkActionBar
          selectedIds={selectedIds}
          projectNames={projectNames}
          statusOptions={bulkStatusOptions}
          projectManagerOptions={projectManagerOptions}
          editAllowed={!!moduleAccess?.project_edit}
          assignPmAllowed={!!moduleFeatures?.project_assign_pm}
          archiveAllowed={!!moduleFeatures?.project_archive}
          onClear={() => onSelectedIdsChange?.([])}
          onAction={onBulkAction!}
        />
      ) : null}
    </div>
  )
}
